'use client'
import {
  Badge,
  Flex,
  IconType,
  List,
  ListIcon,
  ListItem,
} from '@core/ui-elements'
import { useRedirectWithQuery } from '@routes'
import { usePathname } from 'next/navigation'
import { FC } from 'react'

export interface MenuNavigateListItemType {
  link: string
  icon: IconType
  label: string
  badge?: string
  disable?: boolean
  hideItem?: boolean
}

export type MenuNavigateListType = Array<MenuNavigateListItemType>

export const MenuNavigateList: FC<{
  list: MenuNavigateListType
  collapse?: boolean
  handleCollapse: (command?: boolean) => void
}> = ({ list, collapse = false, handleCollapse }) => {
  const pathname = usePathname()

  const { redirectWithQuery } = useRedirectWithQuery()

  const handleRedirect = (isDisabled: boolean, link: string) => {
    if (isDisabled) return null

    if (pathname.startsWith(link)) {
      handleCollapse(true)
      return null
    }

    redirectWithQuery(link)

    handleCollapse(true)
  }

  return (
    <List spacing={1} variant={'menuList'} width={'100%'}>
      {list.map((item: MenuNavigateListItemType, i) => {
        if (item.hideItem) return null
        const isActive = pathname.startsWith(item.link)

        return (
          <ListItem
            key={i}
            aria-disabled={item.disable}
            aria-label={item.label}
            aria-selected={isActive}
            onClick={() => handleRedirect(!!item.disable, item.link)}
          >
            <Flex alignItems={'center'} data-testid="MenuLabel">
              <ListIcon
                aria-selected={isActive}
                as={item.icon}
                boxSize={'1rem'}
                data-testid={item.icon.name}
              />
              {collapse ? null : item.label}
            </Flex>
            {item.badge ? (
              <Badge
                borderRadius={'1.5rem'}
                colorScheme="blue"
                data-testid="MenuBadge"
              >
                {item.badge}
              </Badge>
            ) : null}
          </ListItem>
        )
      })}
    </List>
  )
}
